import { useHandler } from "@redotech/react-util/hook";
import AlertTriangleIcon from "@redotech/redo-icons/alert-triangle.svg";
import FileIcon from "@redotech/redo-icons/file-02.svg";
import XCloseIcon from "@redotech/redo-icons/x-close.svg";
import { toast } from "@redotech/redo-web/alert";
import { RedoSpinner } from "@redotech/redo-web/arbiter-components/loading/redo-spinner";
import { UploaderContext } from "@redotech/redo-web/image-upload";
import type { FileUIPart } from "ai";
import classNames from "classnames";
import {
  memo,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import * as styles from "./ask-redo-attachments.module.css";

/** 20 MiB client-side limit; the server enforces its own limit on top,
 *  but failing fast before the upload is much friendlier UX. */
const MAX_FILE_SIZE_BYTES = 20 * 1024 * 1024;

const MAX_FILE_SIZE_LABEL = "20 MB";

/** Non-image attachment types Ask Redo accepts, keyed by extension
 *  because browsers report an empty `File.type` for some (.csv/.tsv). */
// cspell:ignore spreadsheetml
const NON_IMAGE_EXTENSION_TO_MIME: Record<string, string> = {
  pdf: "application/pdf",
  csv: "text/csv",
  tsv: "text/tab-separated-values",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  xls: "application/vnd.ms-excel",
  json: "application/json",
};

/** The `accept` attribute for the hidden file input. */
export const ASK_REDO_ATTACHMENT_ACCEPT = [
  "image/*",
  ...Object.keys(NON_IMAGE_EXTENSION_TO_MIME).map((ext) => `.${ext}`),
].join(",");

const UNSUPPORTED_TYPE_MESSAGE =
  "That file type isn't supported. Attach an image, PDF, CSV, Excel, TSV, or JSON file.";

/** Media type for the outgoing file part — browser-reported MIME when
 *  present, else inferred from the extension. */
function resolveMediaType(file: File): string | undefined {
  if (file.type) {
    return file.type;
  }
  const ext = extractExtension(file.name);
  return ext ? NON_IMAGE_EXTENSION_TO_MIME[ext] : undefined;
}

function isSupportedAttachment(file: File): boolean {
  if (file.type.startsWith("image/")) {
    return true;
  }
  const ext = extractExtension(file.name);
  if (ext && ext in NON_IMAGE_EXTENSION_TO_MIME) {
    return true;
  }
  return Object.values(NON_IMAGE_EXTENSION_TO_MIME).includes(file.type);
}

/**
 * Lightweight attachment manager for the Ask Redo prompt input.
 *
 * Holds an in-flight list of files the merchant has picked but not
 * yet sent. Each entry is in one of three states:
 *
 *   - `uploading` — upload is in progress; preview chip shows a
 *     spinner overlay over the thumbnail.
 *   - `uploaded` — upload succeeded; entry now carries a real S3
 *     URL that can be sent as a `FileUIPart`.
 *   - `error` — upload failed or was rejected before upload; chip
 *     shows a red overlay with an alert icon. Send is disabled
 *     until the merchant removes the errored entry.
 *
 * The `uploader` comes from `UploaderContext` (provided by
 * `ClientUploader` in `AskRedoLayout`). When no uploader is in
 * the tree, the hook silently no-ops — the picker stays hidden.
 */
export interface Attachment {
  id: string;
  file: File;
  /** Blob URL for instant preview before the upload finishes. We
   *  revoke it when the entry is removed or the input unmounts. */
  previewUrl: string;
  status: "uploading" | "uploaded" | "error";
  /** S3 URL returned by the uploader once `status === "uploaded"`. */
  uploadedUrl?: string;
  error?: string;
  abort: AbortController;
}

export interface UseAttachmentsResult {
  attachments: Attachment[];
  isUploading: boolean;
  hasErrors: boolean;
  /** Build the FileUIPart array to attach to the next sendMessage. */
  toFileParts: () => FileUIPart[];
  /** Add files chosen from a file input or drop event. */
  addFiles: (files: FileList | File[]) => void;
  remove: (id: string) => void;
  /** Drop every entry — call after send so the next message starts
   *  with an empty attachment row. */
  clearAll: () => void;
  /** True iff `UploaderContext` provided an uploader. When false,
   *  the prompt should hide the attach button entirely. */
  uploaderAvailable: boolean;
}

export function useAttachments(): UseAttachmentsResult {
  const uploader = useContext(UploaderContext);
  const [attachments, setAttachments] = useState<Attachment[]>([]);

  // Keep a ref of all blob URLs we've created so unmount-time
  // cleanup can revoke every one even if the state-driven cleanup
  // hasn't run for a particular entry yet.
  const blobUrlsRef = useRef<Set<string>>(new Set());

  // Track all live AbortControllers so we can cancel in-flight
  // uploads on unmount. Without this, an unmounted prompt-input
  // would let its uploads run to completion in the background and
  // trigger a stale `setAttachments` call (harmless React warning,
  // but wasted network / S3 PUTs that the merchant no longer cares
  // about). Entries are added on `addFiles`, removed on success/
  // error/remove/clearAll/unmount.
  const liveControllersRef = useRef<Set<AbortController>>(new Set());

  useEffect(
    () => () => {
      for (const controller of liveControllersRef.current) {
        controller.abort();
      }
      liveControllersRef.current.clear();
      for (const url of blobUrlsRef.current) {
        URL.revokeObjectURL(url);
      }
      blobUrlsRef.current.clear();
    },
    [],
  );

  const addFiles = useHandler(async (input: FileList | File[]) => {
    if (!uploader) {
      return;
    }
    const files = Array.from(input);
    if (files.length === 0) {
      return;
    }

    // Collect the first validation error across all selected files,
    // surface it as an error toast, and skip the invalid files entirely.
    let firstError: string | null = null;
    const validFiles: File[] = [];
    for (const file of files) {
      if (!isSupportedAttachment(file)) {
        firstError ??= UNSUPPORTED_TYPE_MESSAGE;
      } else if (file.size > MAX_FILE_SIZE_BYTES) {
        firstError ??= `File is too large. The maximum size is ${MAX_FILE_SIZE_LABEL}. Please choose a smaller file.`;
      } else {
        validFiles.push(file);
      }
    }
    if (firstError) {
      toast(firstError, { variant: "error" });
    }
    if (validFiles.length === 0) {
      return;
    }

    const entries: Attachment[] = validFiles.map((file) => {
      const previewUrl = URL.createObjectURL(file);
      blobUrlsRef.current.add(previewUrl);
      const abort = new AbortController();
      liveControllersRef.current.add(abort);
      return {
        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        file,
        previewUrl,
        status: "uploading" as const,
        abort,
      };
    });
    setAttachments((prev) => [...prev, ...entries]);

    await Promise.all(
      entries.map(async (entry) => {
        try {
          const result = await uploader({
            file: entry.file,
            signal: entry.abort.signal,
          });
          // Defense in depth: even on success, drop the result if
          // the abort fired (either via remove or unmount). The
          // `setAttachments` map below is also a no-op when the
          // entry is gone, but we'd rather not enqueue the update
          // at all.
          if (entry.abort.signal.aborted) {
            return;
          }
          setAttachments((prev) =>
            prev.map((a) =>
              a.id === entry.id
                ? {
                    ...a,
                    status: "uploaded",
                    uploadedUrl: result.url.toString(),
                  }
                : a,
            ),
          );
        } catch (err) {
          if (entry.abort.signal.aborted) {
            return;
          }

          console.error("[AskRedo] attachment upload failed", err);
          const message = err instanceof Error ? err.message : "Upload failed";
          setAttachments((prev) =>
            prev.map((a) =>
              a.id === entry.id ? { ...a, status: "error", error: message } : a,
            ),
          );
        } finally {
          liveControllersRef.current.delete(entry.abort);
        }
      }),
    );
  });

  const remove = useCallback((id: string) => {
    setAttachments((prev) => {
      const target = prev.find((a) => a.id === id);
      if (target) {
        target.abort.abort();
        liveControllersRef.current.delete(target.abort);
        URL.revokeObjectURL(target.previewUrl);
        blobUrlsRef.current.delete(target.previewUrl);
      }
      return prev.filter((a) => a.id !== id);
    });
  }, []);

  const clearAll = useCallback(() => {
    setAttachments((prev) => {
      for (const a of prev) {
        a.abort.abort();
        liveControllersRef.current.delete(a.abort);
        URL.revokeObjectURL(a.previewUrl);
        blobUrlsRef.current.delete(a.previewUrl);
      }
      return [];
    });
  }, []);

  const toFileParts = useCallback((): FileUIPart[] => {
    return attachments
      .filter(
        (a): a is Attachment & { uploadedUrl: string } =>
          a.status === "uploaded" && typeof a.uploadedUrl === "string",
      )
      .map((a) => ({
        type: "file",
        url: a.uploadedUrl,
        mediaType: resolveMediaType(a.file) ?? "application/octet-stream",
        filename: a.file.name,
      }));
  }, [attachments]);

  return {
    attachments,
    isUploading: attachments.some((a) => a.status === "uploading"),
    hasErrors: attachments.some((a) => a.status === "error"),
    toFileParts,
    addFiles,
    remove,
    clearAll,
    uploaderAvailable: uploader !== undefined,
  };
}

/** Visual row of preview chips, one per attachment. Renders nothing
 *  when the attachment list is empty so the prompt stays compact. */
export const AskRedoAttachmentsRow = memo(function AskRedoAttachmentsRow({
  attachments,
  onRemove,
}: {
  attachments: Attachment[];
  onRemove: (id: string) => void;
}) {
  if (attachments.length === 0) {
    return null;
  }
  return (
    <div className={styles.row}>
      {attachments.map((a) => (
        <AttachmentChip attachment={a} key={a.id} onRemove={onRemove} />
      ))}
    </div>
  );
});

const AttachmentChip = memo(function AttachmentChip({
  attachment,
  onRemove,
}: {
  attachment: Attachment;
  onRemove: (id: string) => void;
}) {
  const isImage = attachment.file.type.startsWith("image/");
  const ext = extractExtension(attachment.file.name);
  const isUploading = attachment.status === "uploading";
  const isError = attachment.status === "error";
  return (
    <div className={classNames(styles.chip, isError && styles.chipError)}>
      {isImage ? (
        <img
          alt={attachment.file.name}
          className={classNames(
            styles.thumbnail,
            isUploading && styles.thumbnailUploading,
          )}
          src={attachment.previewUrl}
        />
      ) : (
        <div
          className={classNames(
            styles.thumbnail,
            styles.fileChip,
            isUploading && styles.thumbnailUploading,
          )}
        >
          <FileIcon aria-hidden="true" className={styles.fileChipIcon} />
          {ext && <span className={styles.fileChipExt}>{ext}</span>}
        </div>
      )}
      {isUploading && (
        <div
          aria-label="Uploading"
          className={styles.spinnerBadge}
          title="Uploading"
        >
          <RedoSpinner size="xs" style="track" />
        </div>
      )}
      {isError && (
        <div
          aria-label={attachment.error ?? "Upload failed"}
          className={styles.errorBar}
          title={attachment.error ?? "Upload failed"}
        >
          <AlertTriangleIcon aria-hidden="true" className={styles.errorIcon} />
          Failed
        </div>
      )}
      <button
        aria-label={`Remove ${attachment.file.name}`}
        className={styles.removeButton}
        onClick={() => onRemove(attachment.id)}
        type="button"
      >
        <XCloseIcon aria-hidden="true" className={styles.removeIcon} />
      </button>
    </div>
  );
});

function extractExtension(filename: string): string | null {
  const dot = filename.lastIndexOf(".");
  if (dot < 0 || dot === filename.length - 1) {
    return null;
  }
  const ext = filename.slice(dot + 1);
  if (ext.length > 5) {
    return null;
  }
  return ext.toLowerCase();
}

// Re-export for callers that want to type their own helpers.

// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export { type Uploader } from "@redotech/redo-web/image-upload";
